// import { API_URL } from "../const";
import axios from "axios";
import { decryptData } from "./enode-decode";
import { API_URL, s3BaseUrl } from "../const";

/** Build <img> src for uploads from local API (fileUpload) or legacy S3-relative paths. */
export const resolveUploadImageSrc = (
  photo: string | undefined | null,
): string => {
  const p = (photo ?? "").trim();
  if (!p) return "";
  if (p.startsWith("data:image")) return p;
  if (/^https?:\/\//i.test(p)) return p;
  const base = API_URL.replace(/\/+$/, "");
  const path = p.replace(/^\/+/, "");
  if (/^-+api\/uploads\//i.test(path)) {
    return `${base}/uploads/${path.replace(/^-+api\/uploads\/?/i, "")}`;
  }
  if (path.startsWith("uploads/")) return `${base}/${path}`;
  if (
    path.startsWith("NewProduct/") ||
    path.startsWith("Company-") ||
    path.includes("CompanyImages/") ||
    path.includes("/User-")
  ) {
    return `${base}/uploads/${path}`;
  }
  return `${s3BaseUrl}${path}`;
};

/** Extract login user array from { StatusCode, Response: { data: [...] } } or { data: { ... } }. Used by login reducer and selector. */
export const getLoginDataArray = (payload: any): any[] => {
  if (!payload || typeof payload !== "object") return [];
  if (typeof payload === "string") {
    try {
      const parsed = JSON.parse(payload);
      return getLoginDataArray(parsed);
    } catch {
      return [];
    }
  }
  const body =
    payload?.data &&
    typeof payload.data === "object" &&
    !Array.isArray(payload.data)
      ? payload.data
      : payload;
  const inner = body?.Response ?? body?.response ?? body?.result;
  const raw = inner?.data ?? inner?.Data;
  if (Array.isArray(raw) && raw.length > 0) {
    return raw.filter((item: any) => item != null && typeof item === "object");
  }
  if (raw != null && typeof raw === "object" && !Array.isArray(raw)) {
    return [raw];
  }
  return [];
};

/** Normalize decrypted list payloads (APIs vary: data, Data, rows, or root array). */
export function normalizedDecodedArray(docodeVal: any): any[] {
  if (docodeVal == null) return [];
  if (Array.isArray(docodeVal)) return docodeVal;
  if (typeof docodeVal !== "object") return [];
  const arr =
    docodeVal.data ??
    docodeVal.Data ??
    docodeVal.rows ??
    docodeVal.Rows ??
    docodeVal.Items ??
    docodeVal.items;
  if (Array.isArray(arr)) return arr;
  return [];
}

/**
 * Company-wide list/detail decode: encrypted hex in `response` or `Response` (string),
 * or plain JSON with nested `data` (e.g. `{ Response: { data: [...] } }`).
 */
export const docodeHexResponse = (response: any, key: string) => {
  try {
    if (!response || typeof response !== "object") {
      return [];
    }
    const hex =
      response.response ??
      response.Response ??
      (typeof response.data === "string" ? response.data : undefined);
    if (typeof hex === "string" && hex.trim() !== "") {
      const docodeVal = decryptData(hex, key);
      const fromEncrypted = normalizedDecodedArray(docodeVal);
      if (fromEncrypted.length > 0) return fromEncrypted;
    }
    const fromPlain = normalizedDecodedArray(
      response.Response ?? response.response ?? response,
    );
    if (fromPlain.length > 0) {
      return fromPlain;
    }
    const fromRoot = normalizedDecodedArray(response);
    if (fromRoot.length > 0) {
      return fromRoot;
    }
    console.warn("Invalid response structure for decoding:", response);
    return [];
  } catch (error) {
    console.error("Error in docodeHexResponse:", error);
    return [];
  }
};

// export const fileUploadServer = async (
//   result: any,
//   savePath: any,
//   companyID: any,
//   directoryPath: any,
// ) => {
//   const formData = new FormData();
//   formData.append("photoimg", result);
//   formData.append("file", result);
//   formData.append("savePath", savePath);
//   formData.append("companyID", companyID);
//   formData.append("directoryPath", directoryPath);

//   const response = await fetch(`${API_URL}_common/uploadImage.php`, {
//     method: "POST",
//     body: formData,
//   });
//   if (!response.ok) {
//     console.error(
//       "Server returned an error:",
//       response.status,
//       response.statusText,
//     );
//     return false;
//   }

//   const responseJson = await response.json();
//   if (responseJson.status === "Success") {
//     return responseJson.file_path;
//   }
//   return false;
// };

export const uploadImageMinIO = async (
  file: any,
  path: any,
  imageName: any,
) => {
  try {
    if (!file || !path || !imageName) {
      throw new Error("Missing required parameters");
    }

    const formData = new FormData();

    formData.append("photoimg", file);
    formData.append("typeval", path);
    formData.append("imageNameVal", imageName);
    formData.append("fileFormat", "Image");

    const apiURL = "https://phoenixapi.squarenow.co.in/";
    console.log("Upload URL:", `${apiURL}UploadImage.php`);
    console.log("Upload parameters:", { path, imageName, fileType: file.type });

    const response = await fetch(`${apiURL}UploadImage.php`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      console.error(
        "Server returned an error:",
        response.status,
        response.statusText,
      );
      throw new Error(
        `Upload failed: ${response.status} ${response.statusText}`,
      );
    }

    const responseText = await response.json();
    console.log("Raw response:", responseText);

    if (responseText.status === "Success") {
      return responseText.response;
    }
    throw new Error("Upload failed: Invalid response status");
  } catch (error) {
    console.error("Error in uploadImageMinIO:", error);
    throw error;
  }
};

export const uploadFileMinIO = async (
  file: File,
  path: string,
  fileName: string,
) => {
  try {
    if (!file || !path || !fileName) {
      throw new Error("Missing required parameters");
    }

    const formData = new FormData();
    formData.append("photoimg", file);
    formData.append("typeval", path);
    formData.append("imageNameVal", fileName);

    // Determine file format (Image, Document, etc.)
    const imageTypes = ["image/jpeg", "image/png", "image/jpg"];
    const documentTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    let fileFormat = "Other";
    if (imageTypes.includes(file.type)) {
      fileFormat = "Image";
    } else if (documentTypes.includes(file.type)) {
      fileFormat = "Document";
    }

    formData.append("fileFormat", fileFormat);

    const apiURL = "https://phoenixapi.squarenow.co.in/";

    const response = await fetch(`${apiURL}UploadImage.php`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      console.error(
        "Server returned an error:",
        response.status,
        response.statusText,
      );
      throw new Error(
        `Upload failed: ${response.status} ${response.statusText}`,
      );
    }

    const responseJson = await response.json();
    console.log("Raw response:", responseJson);

    if (responseJson.status === "Success") {
      return responseJson.response;
    }

    throw new Error("Upload failed: Invalid response status");
  } catch (error) {
    console.error("Error in uploadFileMinIO:", error);
    throw error;
  }
};

//IMAGE UPLOAD TO SERVER
// export const imageUploadToS3 = async (
//   result: any,
//   path: any,
//   fileType?: any,
//   imageName?: any,
// ) => {
//   const formData = new FormData();
//   formData.append("photoimg", result);
//   formData.append("typeval", path);
//   formData.append("fileFormat", fileType ? fileType : "Image");
//   formData.append("imageNameVal", imageName);
//   const apiURL = "https://phoenixapi.squarenow.co.in/";

//   // const response = await fetch(`${apiURL}UploadImage.php`, {
//   const response = await fetch(`${apiURL}_common/imageUploadS3.php`, {
//     method: "POST",
//     body: formData,
//   });

//   // const response = await fetch(`${API_URL}_common/uploadImageToServer.php`, {
//   //   method: "POST",
//   //   body: formData,
//   // });
//   if (!response.ok) {
//     console.error(
//       "Server returned an error:",
//       response.status,
//       response.statusText,
//     );
//     // return "Image Upload Failed";
//   } else {
//     const responseJson = await response.json();
//     if (responseJson.status === "Success") {
//       return responseJson.response;
//     } else if ("error_message" in responseJson) {
//       return "Image Upload Failed";
//     }
//   }

//   // } catch (error) {
//   //   console.error('Caught error:', error);
//   //   // return "Image Upload Failed";
//   // }
// };

//IMAGE UPLOAD TO SERVER
export const imageUploadToS3 = async (
  result: any,
  path: any,
  fileType: any,
  imageName: any,
) => {
  const formData = new FormData();
  formData.append("photoimg", result);
  formData.append("typeval", path);
  formData.append("fileFormat", fileType ? fileType : "Image");
  formData.append("imageNameVal", imageName ?? Date.now().toString());

  // const apiURL = "https://phoenixapi.squarenow.co.in/";
  // const response = await fetch(`${API_URL}FileUpload.php`, {
  const response = await fetch(`${API_URL}api/fileUpload`, {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    console.error(
      "Server returned an error:",
      response.status,
      response.statusText,
    );
    // return "Image Upload Failed";
  } else {
    const responseJson = await response.json();
    if (responseJson.status === "Success") {
      return responseJson;
    } else if ("error_message" in responseJson) {
      return "Image Upload Failed";
    }
  }
};

export const maxDOBLimit = () => {
  const today = new Date();
  const maxDOB = new Date(
    today.getFullYear() - 19,
    today.getMonth(),
    today.getDate(),
  );
  return maxDOB.toISOString().split("T")[0]; // Returns a string in 'YYYY-MM-DD' format
};

// export const searchCompanies = async (SearchValues: string) => {
//   try {
//     if (!SearchValues) {
//       throw new Error("Missing required parameters");
//     }

//     const formData = new FormData();

//     formData.append("SearchValue", SearchValues);
//     const ApiUrl = "https://api.phoenixcfportal.co.uk/SearchCompanies.php";

//     // console.log("Upload URL:", `${API_URL}SearchCompanies.php`);
//     console.log("Upload URL:", `${ApiUrl}SearchCompanies.php`);
//     // console.log("Upload parameters:", { path, imageName, fileType: file.type });
//     // const response = await fetch(`${ApiUrl}SearchCompanies.php`, {
//     //   method: "POST",
//     //   body: formData,
//     // });
//     const response  = axios.get(ApiUrl,)

//     console.log("response", response);

//     if (!response.ok) {
//       console.error(
//         "Server returned an error:",
//         response.status,
//         response.statusText,
//       );
//       throw new Error(
//         `Upload failed: ${response.status} ${response.statusText}`,
//       );
//     }

//     const responseText = await response.json();
//     console.log("Raw response:", responseText);

//     if (responseText.status === "Success") {
//       return responseText.response;
//     }
//     throw new Error("Upload failed: Invalid response status");
//   } catch (error) {
//     console.error("Error in uploadImageMinIO:", error);
//     throw error;
//   }
// };

export const searchCompanies = async (SearchValues: string) => {
  try {
    if (!SearchValues) {
      throw new Error("Missing required parameters");
    }

    const ApiUrl = "https://api.phoenixcfportal.co.uk/SearchCompanies.php";

    const response = await axios.get(ApiUrl, {
      params: {
        SearchValue: SearchValues, // passed as a query string
      },
    });
    console.log("response", response);

    if (response.data && Array.isArray(response.data.items)) {
      return response.data.items;
    } else {
      throw new Error("Unexpected response format");
    }
  } catch (error) {
    console.error("Error in searchCompanies:", error);
    throw error;
  }
};

export const searchCompaniesNumber = async (SearchValues: string) => {
  try {
    if (!SearchValues) {
      throw new Error("Missing required parameters");
    }

    const ApiUrl =
      "https://api.phoenixcfportal.co.uk/SearchCompaniesNumber.php";

    const response = await axios.get(ApiUrl, {
      params: {
        SearchValue: SearchValues, // passed as a query string
      },
    });
    console.log(response);
    if (response.data && typeof response.data === "object") {
      console.log(response.data);
      return response.data;
    } else {
      throw new Error("Unexpected response format");
    }
  } catch (error) {
    console.error("Error in searchCompanies:", error);
    throw error;
  }
};

// Add this function outside your component
// export const getProductSwitchStates = (productCode: any, ListProductArr: any) => {
//   debugger;
//   if (!productCode || !ListProductArr?.length) return null;

//   const selectedProduct = ListProductArr.find(
//     (item: any) => item.productCode === productCode,
//   );

//   if (!selectedProduct) return null;

//   return {
//     IsAnnualTurnOver:
//       selectedProduct.IsAnnualTurnOver === "1" ||
//       selectedProduct.IsAnnualTurnOver === 1,
//     IsAmountRequested:
//       selectedProduct.IsAmountRequested === "1" ||
//       selectedProduct.IsAmountRequested === 1,
//     IsPurposeOfFund:
//       selectedProduct.IsPurposeOfFund === "1" ||
//       selectedProduct.IsPurposeOfFund === 1,
//     IsPropertyType:
//       selectedProduct.IsPropertyType === "1" ||
//       selectedProduct.IsPropertyType === 1,
//     IsPropertyAddressLine1:
//       selectedProduct.IsPropertyAddressLine1 === "1" ||
//       selectedProduct.IsPropertyAddressLine1 === 1,
//     IsPropertyValue:
//       selectedProduct.IsPropertyValue === "1" ||
//       selectedProduct.IsPropertyValue === 1,
//     IsRemainingValueOfmortgageOnProperty:
//       selectedProduct.IsRemainingValueOfmortgageOnProperty === "1" ||
//       selectedProduct.IsRemainingValueOfmortgageOnProperty === 1,
//     IsTermOfFundRequired:
//       selectedProduct.IsTermOfFundRequired === "1" ||
//       selectedProduct.IsTermOfFundRequired === 1,
//     IsNoOfLocation:
//       selectedProduct.IsNoOfLocation === "1" ||
//       selectedProduct.IsNoOfLocation === 1,
//     IsMonthlyCardTaking:
//       selectedProduct.IsMonthlyCardTaking === "1" ||
//       selectedProduct.IsMonthlyCardTaking === 1,
//   };
// };

//   export const getProductSwitchStates = (
//   productCode: any,
//   ListProductArr: any[],
// ) => {
//   if (!productCode || !ListProductArr?.length) return null;

//   const selectedProduct = ListProductArr.find(
//     (item: any) => item.productCode === productCode,
//   );

//   if (!selectedProduct) return null;

//   //  Store all converted values first
//   const switchStates = {
//     IsAnnualTurnOver: Boolean(selectedProduct.IsAnnualTurnOver),
//     IsAmountRequested: Boolean(selectedProduct.IsAmountRequested),
//     IsPurposeOfFund: Boolean(selectedProduct.IsPurposeOfFund),
//     IsPropertyType: Boolean(selectedProduct.IsPropertyType),
//     IsPropertyAddressLine1: Boolean(
//       selectedProduct.IsPropertyAddressLine1,
//     ),
//     IsPropertyValue: Boolean(selectedProduct.IsPropertyValue),
//     IsRemainingValueOfmortgageOnProperty: Boolean(
//       selectedProduct.IsRemainingValueOfmortgageOnProperty,
//     ),
//     IsTermOfFundRequired: Boolean(
//       selectedProduct.IsTermOfFundRequired,
//     ),
//     IsNoOfLocation: Boolean(selectedProduct.IsNoOfLocation),
//     IsMonthlyCardTaking: Boolean(
//       selectedProduct.IsMonthlyCardTaking,
//     ),
//   };
//   console.log("switchStates" ,switchStates);

//   //  Return all required data
//   return {
//     productCode: selectedProduct.productCode,
//     productID: selectedProduct.productID,
//     productName: selectedProduct.productName,
//     switchStates,
//   };
// };

// In your common.utils.js file, update the function:
/** True when product row has switch on (API may use PascalCase or camelCase). */
export const readProductSwitchOn = (p: any, ...keys: string[]) => {
  for (const k of keys) {
    const v = p?.[k];
    if (
      v === true ||
      v === 1 ||
      v === "1" ||
      v === "true" ||
      String(v).toLowerCase() === "yes"
    ) {
      return true;
    }
  }
  return false;
};

/**
 * Resolves product switches for proposals. The leads dropdown list often omits
 * granular flags; pass `enrichProductList` (full product grid API) to merge them.
 */
export const getProductSwitchStates = (
  productCode: any,
  ListProductArr: any,
  enrichProductList?: any,
  productID?: any,
) => {
  const listA = Array.isArray(ListProductArr) ? ListProductArr : [];
  const listB = Array.isArray(enrichProductList) ? enrichProductList : [];
  if (!listA.length && !listB.length) return null;

  const codeIn =
    productCode != null && productCode !== "" ? String(productCode) : "";

  let selectedProduct: any = null;
  if (codeIn && listA.length) {
    selectedProduct = listA.find((item: any) => {
      const c = item?.productCode ?? item?.ProductCode;
      return c != null && String(c) === codeIn;
    });
  }
  if (!selectedProduct && productID != null && listA.length) {
    selectedProduct = listA.find(
      (item: any) =>
        String(item.productID ?? item.ProductID) === String(productID),
    );
  }
  if (!selectedProduct && productID != null && listB.length) {
    selectedProduct = listB.find(
      (item: any) =>
        String(item.productID ?? item.ProductID) === String(productID),
    );
  }

  if (!selectedProduct) return null;

  let source = selectedProduct;
  if (listB.length) {
    const pid = source.productID ?? source.ProductID;
    const enrich = listB.find(
      (item: any) => String(item.productID ?? item.ProductID) === String(pid),
    );
    if (enrich) {
      source = { ...source, ...enrich };
    }
  }

  const code = String(
    source.productCode ?? source.ProductCode ?? codeIn ?? "",
  );

  // For ALL products, use switch states from database
  // If switch states are not in DB, use fallback for common products
  let switchStates = {
    IsAnnualTurnOver:
      source.IsAnnualTurnOver === "1" || source.IsAnnualTurnOver === 1,
    IsAmountRequested:
      source.IsAmountRequested === "1" || source.IsAmountRequested === 1,
    IsPurposeOfFund:
      source.IsPurposeOfFund === "1" || source.IsPurposeOfFund === 1,
    IsPropertyType:
      source.IsPropertyType === "1" || source.IsPropertyType === 1,
    IsPropertyAddressLine1:
      source.IsPropertyAddressLine1 === "1" ||
      source.IsPropertyAddressLine1 === 1,
    IsPropertyValue:
      source.IsPropertyValue === "1" || source.IsPropertyValue === 1,
    IsRemainingValueOfmortgageOnProperty:
      source.IsRemainingValueOfmortgageOnProperty === "1" ||
      source.IsRemainingValueOfmortgageOnProperty === 1,
    IsTermOfFundRequired:
      source.IsTermOfFundRequired === "1" || source.IsTermOfFundRequired === 1,
    IsNoOfLocation: readProductSwitchOn(
      source,
      "IsNoOfLocation",
      "isNoOfLocation",
    ),
    IsMonthlyCardTaking:
      source.IsMonthlyCardTaking === "1" || source.IsMonthlyCardTaking === 1,
    IsOverdraftLimit:
      source.IsOverdraftLimit === "1" ||
      source.IsOverdraftLimit === 1 ||
      source.isOverdraftLimit === "1" ||
      source.isOverdraftLimit === 1,
    IshowSoonFundNeeded:
      source.IshowSoonFundNeeded === "1" ||
      source.IshowSoonFundNeeded === 1 ||
      source.isHowSoonFundNeeded === "1" ||
      source.isHowSoonFundNeeded === 1,
  };

  // If no switch states in DB for common products, use fallback
  if (!source.IsAnnualTurnOver && ["PF", "CFF", "BCA"].includes(code)) {
    // Set fallback switch states for common products
    if (code === "PF") {
      switchStates = {
        IsAnnualTurnOver: false,
        IsAmountRequested: true,
        IsPurposeOfFund: true,
        IsPropertyType: true,
        IsPropertyAddressLine1: true,
        IsPropertyValue: true,
        IsRemainingValueOfmortgageOnProperty: true,
        IsTermOfFundRequired: true,
        IsNoOfLocation: false,
        IsMonthlyCardTaking: false,
        IshowSoonFundNeeded: false,
        IsOverdraftLimit: false,
      };
    } else if (code === "CFF") {
      switchStates = {
        IsAnnualTurnOver: true,
        IsAmountRequested: true,
        IsPurposeOfFund: true,
        IsPropertyType: false,
        IsPropertyAddressLine1: false,
        IsPropertyValue: false,
        IsRemainingValueOfmortgageOnProperty: false,
        IsTermOfFundRequired: true,
        IsNoOfLocation: false,
        IsMonthlyCardTaking: false,
        IshowSoonFundNeeded: false,
        IsOverdraftLimit: false,
      };
    } else if (code === "BCA") {
      switchStates = {
        IsAnnualTurnOver: true,
        IsAmountRequested: true,
        IsPurposeOfFund: true,
        IsPropertyType: false,
        IsPropertyAddressLine1: false,
        IsPropertyValue: false,
        IsRemainingValueOfmortgageOnProperty: false,
        IsTermOfFundRequired: false,
        IsNoOfLocation: true,
        IsMonthlyCardTaking: true,
        IshowSoonFundNeeded: true,
        IsOverdraftLimit: false,
      };
    }
  }

  const granularSwitches = {
    isMerchantProvider: readProductSwitchOn(
      source,
      "IsMerchantProvider",
      "isMerchantProvider",
    ),
    IsNoCardTerminals: readProductSwitchOn(
      source,
      "IsNoCardTerminals",
      "isNoCardTerminals",
    ),
    IsAnnualCardTurnoverPerOutlet: readProductSwitchOn(
      source,
      "IsAnnualCardTurnoverPerOutlet",
      "isAnnualCardTurnoverPerOutlet",
    ),
    IsAvgTransactionValue: readProductSwitchOn(
      source,
      "IsAvgTransactionValue",
      "isAvgTransactionValue",
    ),
    IsPaymentsOverPhoneMOTO: readProductSwitchOn(
      source,
      "IsPaymentsOverPhoneMOTO",
      "isPaymentsOverPhoneMOTO",
    ),
    IsEposName: readProductSwitchOn(source, "IsEposName", "isEposName"),
    IsOnlinePayments: readProductSwitchOn(
      source,
      "IsOnlinePayments",
      "isOnlinePayments",
    ),
    IsWhatIsThesettlementTime: readProductSwitchOn(
      source,
      "IsWhatIsThesettlementTime",
      "isWhatIsThesettlementTime",
    ),
    IsBankingWindowClose: readProductSwitchOn(
      source,
      "IsBankingWindowClose",
      "isBankingWindowClose",
    ),
    IsNoOfEpos: readProductSwitchOn(source, "IsNoOfEpos", "isNoOfEpos"),
    IsCurrentProvider: readProductSwitchOn(
      source,
      "IsCurrentProvider",
      "isCurrentProvider",
    ),
    IsNumberOfOutlets: readProductSwitchOn(
      source,
      "IsNumberOfOutlets",
      "isNumberOfOutlets",
    ),
    IsNumberOfCardMachines: readProductSwitchOn(
      source,
      "IsNumberOfCardMachines",
      "isNumberOfCardMachines",
    ),
  };

  return { switchStates: { ...switchStates, ...granularSwitches } };
};
